import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../services/api";
import { AuthContext } from "../contexts/AuthContext";

// ── Inline Button ─────────────────────────────────────────────────────────────
function Button({ children, className = "", onClick, disabled, type = "button", ...props }) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center justify-center font-semibold transition-all cursor-pointer select-none disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

// ── Live feed ticker ──────────────────────────────────────────────────────────
function LiveFeed() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    const pool = [
      "🔥 New user activated",
      "📞 Call answered",
      "💬 SMS received",
      "🎥 Meeting started",
    ];
    const i = setInterval(() => {
      setEvents((e) => [pool[Math.floor(Math.random() * pool.length)], ...e.slice(0, 3)]);
    }, 1500);
    return () => clearInterval(i);
  }, []);
  return (
    <div className="fixed bottom-6 left-6 z-50 space-y-2">
      {events.map((e, i) => (
        <motion.div
          key={`${e}-${i}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-black/80 px-4 py-2 rounded-xl border border-white/10 text-xs text-white"
        >
          {e}
        </motion.div>
      ))}
    </div>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
}

// ── Format phone number for display ──────────────────────────────────────────
function formatPhone(num) {
  const digits = num.replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1") {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return num;
}

// ── Main signup flow ──────────────────────────────────────────────────────────
export default function Signup() {
  const navigate = useNavigate();
  const { register } = useContext(AuthContext);

  const [step, setStep] = useState(1);

  // Step 1
  const [email, setEmail] = useState("");

  // Step 2
  const [company, setCompany] = useState("");
  const [password, setPassword] = useState("");
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState("");

  // Step 3
  const [areaCode, setAreaCode] = useState("");
  const [numbers, setNumbers] = useState([]);
  const [numLoading, setNumLoading] = useState(false);
  const [numError, setNumError] = useState("");
  const [selectedNumber, setSelectedNumber] = useState("");
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState("");

  // Step 4
  const [finalNumber, setFinalNumber] = useState("");

  // ── Step 1 → 2 ──────────────────────────────────────────────────────────────
  function handleEmailContinue() {
    if (!email.trim() || !email.includes("@")) return;
    setStep(2);
  }

  // ── Step 2: Register ─────────────────────────────────────────────────────────
  async function handleRegister() {
    if (!company.trim() || !password.trim()) return;
    setRegLoading(true);
    setRegError("");
    const result = await register(company.trim(), email.trim(), password);
    setRegLoading(false);
    if (result.success) {
      setStep(3);
    } else {
      setRegError(result.error || "Registration failed");
    }
  }

  // ── Step 3: Search numbers ───────────────────────────────────────────────────
  async function handleSearch() {
    if (areaCode.length < 3) return;
    setNumLoading(true);
    setNumError("");
    setNumbers([]);
    setSelectedNumber("");
    try {
      const res = await api.get(`/carrier/signup-numbers?areaCode=${areaCode}`);
      if (res.data.length === 0) {
        setNumError("No numbers found for that area code. Try another.");
      } else {
        setNumbers(res.data);
        setSelectedNumber(res.data[0].phoneNumber);
      }
    } catch {
      setNumError("Could not fetch numbers. Try again.");
    }
    setNumLoading(false);
  }

  // ── Step 3 → 4: Provision ───────────────────────────────────────────────────
  async function handleProvision() {
    if (!selectedNumber) return;
    setProvisioning(true);
    setProvisionError("");
    try {
      const res = await api.post("/carrier/signup-provision", { phoneNumber: selectedNumber });
      setFinalNumber(res.data.phoneNumber || selectedNumber);
      setStep(4);
    } catch (err) {
      setProvisionError(
        err.response?.data?.message || "Failed to provision number. Please try again."
      );
    }
    setProvisioning(false);
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
      <LiveFeed />

      {/* Nav logo */}
      <div className="fixed top-0 left-0 right-0 z-30 flex items-center px-6 py-3 border-b border-white/10 bg-black/80 backdrop-blur-xl">
        <img
          src="/logo.png"
          alt="ONLYnumber.online"
          className="h-14 w-auto object-contain cursor-pointer"
          style={{ maxWidth: 240 }}
          onClick={() => navigate("/")}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md mt-20"
      >
        {/* Heading */}
        <h1 className="text-4xl font-bold text-center">
          {step < 4 ? "You're seconds away." : "You're live. 🎉"}
        </h1>
        <p className="text-center text-white/60 mt-3">
          {step < 4
            ? "Set up your system in 4 quick steps."
            : "Your number is ready to receive calls."}
        </p>

        {/* Step dots */}
        <div className="flex justify-center gap-2 mt-6">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`h-2 w-10 rounded-full transition-all duration-300 ${
                step >= i ? "bg-white" : "bg-white/20"
              }`}
            />
          ))}
        </div>

        {/* Steps */}
        <div className="mt-8 space-y-4">
          <AnimatePresence mode="wait">

            {/* Step 1 — Email */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <input
                  placeholder="Work email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleEmailContinue()}
                  autoFocus
                  className="w-full p-4 rounded-xl bg-black border border-white/10 text-white placeholder-white/40 outline-none focus:border-white/40"
                />
                <Button
                  className="w-full h-14 rounded-xl bg-white text-black hover:bg-gray-100 text-base"
                  onClick={handleEmailContinue}
                  disabled={!email.trim() || !email.includes("@")}
                >
                  Continue
                </Button>
              </motion.div>
            )}

            {/* Step 2 — Company + Password */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <input
                  placeholder="Company / Business name"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  autoFocus
                  className="w-full p-4 rounded-xl bg-black border border-white/10 text-white placeholder-white/40 outline-none focus:border-white/40"
                />
                <input
                  placeholder="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleRegister()}
                  className="w-full p-4 rounded-xl bg-black border border-white/10 text-white placeholder-white/40 outline-none focus:border-white/40"
                />
                {regError && (
                  <p className="text-red-400 text-sm text-center">{regError}</p>
                )}
                <Button
                  className="w-full h-14 rounded-xl bg-white text-black hover:bg-gray-100 text-base"
                  onClick={handleRegister}
                  disabled={regLoading || !company.trim() || !password.trim()}
                >
                  {regLoading ? <Spinner /> : "Next"}
                </Button>
              </motion.div>
            )}

            {/* Step 3 — Pick a number */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <p className="text-sm text-white/50 text-center">
                  Enter an area code to find available numbers
                </p>
                <div className="flex gap-2">
                  <input
                    placeholder="e.g. 702"
                    value={areaCode}
                    onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    maxLength={3}
                    autoFocus
                    className="flex-1 p-4 rounded-xl bg-black border border-white/10 text-white placeholder-white/40 outline-none focus:border-white/40 text-center text-lg tracking-widest"
                  />
                  <Button
                    className="px-6 rounded-xl border border-white/20 text-white hover:bg-white/10"
                    onClick={handleSearch}
                    disabled={numLoading || areaCode.length < 3}
                  >
                    {numLoading ? <Spinner /> : "Search"}
                  </Button>
                </div>

                {numError && (
                  <p className="text-red-400 text-sm text-center">{numError}</p>
                )}

                {numbers.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {numbers.map((n) => (
                      <label
                        key={n.phoneNumber}
                        className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                          selectedNumber === n.phoneNumber
                            ? "border-white bg-white/10"
                            : "border-white/10 hover:border-white/30"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="number"
                            value={n.phoneNumber}
                            checked={selectedNumber === n.phoneNumber}
                            onChange={() => setSelectedNumber(n.phoneNumber)}
                            className="accent-white"
                          />
                          <span className="font-mono text-sm">{formatPhone(n.phoneNumber)}</span>
                        </div>
                        <span className="text-xs text-white/50">
                          {n.locality || n.region || "US"}
                        </span>
                      </label>
                    ))}
                  </div>
                )}

                {provisionError && (
                  <p className="text-red-400 text-sm text-center">{provisionError}</p>
                )}

                <Button
                  className="w-full h-14 rounded-xl bg-white text-black hover:bg-gray-100 text-base"
                  onClick={handleProvision}
                  disabled={!selectedNumber || provisioning}
                >
                  {provisioning ? (
                    <span className="flex items-center gap-2">
                      <Spinner /> Activating your number…
                    </span>
                  ) : (
                    "Activate Number"
                  )}
                </Button>
              </motion.div>
            )}

            {/* Step 4 — Done */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center space-y-6"
              >
                <div className="text-2xl font-bold text-green-400">✓ System Active</div>
                <div className="p-6 border border-white/10 rounded-xl bg-white/5">
                  <div className="text-sm text-white/60">Your Number</div>
                  <div className="text-2xl font-bold mt-1 font-mono">
                    {formatPhone(finalNumber)}
                  </div>
                  <div className="text-xs text-white/40 mt-2">
                    Ready to receive calls and SMS
                  </div>
                </div>
                <div className="space-y-3">
                  <Button
                    className="w-full h-14 rounded-xl bg-white text-black hover:bg-gray-100 text-base"
                    onClick={() => navigate("/dashboard")}
                  >
                    Open Dashboard
                  </Button>
                  <Button
                    className="w-full h-12 rounded-xl border border-white/20 text-white hover:bg-white/10 text-sm"
                    onClick={() => navigate("/dashboard")}
                  >
                    Invite Team
                  </Button>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>

        <div className="text-center text-xs text-white/40 mt-6">
          No credit card • Live instantly
        </div>
        <div className="text-center text-xs text-white/30 mt-2">
          Already have an account?{" "}
          <button
            className="text-white/60 underline hover:text-white"
            onClick={() => navigate("/login")}
          >
            Sign in
          </button>
        </div>
      </motion.div>
    </div>
  );
}
